import { getSiteConfig } from '@shared/config';
import { useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';

type Translator = ReturnType<typeof useTranslations>;
type Formatter = ReturnType<typeof useCurrencyFormatter>;
type PrizeTableNumberFormat = ReturnType<typeof getSiteConfig>['CURRENCY_DISPLAY']['prizeTableNumberFormat'];

/**
 * Helper function, that will return correct translation for 0 or missing prize values
 * @param prize - prize amount
 * @param t - translations function
 * @returns translated prize value to display
 */
const getMissingPrizeValue = (prize: number | undefined, t: Translator) => {
  return prize === 0 ? t('results_prize_no_hits') : t('results_prize_missing_value');
};

/**
 * Helper function, that will apply currency conversion to provided prize value
 * @param prize - prize amount in minor units
 * @param cf - formatter function
 * @param format - market prize-table number format
 * @returns formated prize value to display
 */
const getFormattedPrize = (prize: number, cf: Formatter, format: PrizeTableNumberFormat) => cf(prize).format(format).get();

/**
 * Default getPrize method
 * @param prize - prize amount in minor units
 * @param t - translations function
 * @param cf - formatter function
 * @param format - market prize-table number format
 * @returns Properly translated/formatted prize value to display
 */
const getPrize = (prize: number | undefined, t: Translator, cf: Formatter, format: PrizeTableNumberFormat) => {
  return prize && prize > 0 ? getFormattedPrize(prize, cf, format) : getMissingPrizeValue(prize, t);
};

/**
 * Special getPrize use case, for megaMillions. Key difference from default:
 *  - apply provided multiplier to all value, apart from rank === 1
 *  - if (for some reason) multiplier is not available, method will not crash, but will return 0, indicating potential problem
 * @param prize - prize amount in minor units
 * @param rank - current prize table tier
 * @param multiplier - selected multiplier value
 * @param t - translations function
 * @param cf - formatter function
 * @param format - market prize-table number format
 * @returns Properly translated/formatted prize value to display
 */
const getPrizeMegaMillions = (prize: number | undefined, rank: string, multiplier: number, t: Translator, cf: Formatter, format: PrizeTableNumberFormat) => {
  if (!prize || prize === 0) return getMissingPrizeValue(prize, t);
  const multiplierValue = rank !== '1' ? (multiplier ?? 0) : 1;
  return getFormattedPrize(prize * multiplierValue, cf, format);
};

/**
 * Hook, that will provide prize calculation logic for results prize tables
 * @param namespace - namespace, to resolve missing prize scenarios
 * @returns Object, with avaialble methods, to be used depending on the lottery
 */
function useGetPrize(namespace: string) {
  const t = useTranslations(namespace);
  const cf = useCurrencyFormatter();
  const { prizeTableNumberFormat } = getSiteConfig().CURRENCY_DISPLAY;

  return {
    default: (prize: number | undefined) => getPrize(prize, t, cf, prizeTableNumberFormat),
    megaMillions: (prize: number | undefined, rank: string, multiplier: number) => getPrizeMegaMillions(prize, rank, multiplier, t, cf, prizeTableNumberFormat),
  };
}

export { getFormattedPrize, getMissingPrizeValue, getPrize, getPrizeMegaMillions, useGetPrize };
